// Permanent redirects for old Webflow URLs. Single source of truth for:
// - the root route's beforeLoad (client-side, incl. the SPA shell fallback)
// - scripts/preview.ts (mimics the CDN locally)
// - the CloudFront Function generated by scripts/gen-cloudfront-function.ts.
//   Its handler is ES5 and re-implements `resolveRequest` below by hand;
//   scripts/gen-cloudfront-function.test.ts runs it against this file's
//   functions, so edit both together or that test fails.
// Query-string redirects (`queryRedirects`) are only seen by the CDN function and
// the router, not the prerenderer.
// Redirect sources are excluded from prerendering (see vite.config.ts).
import { NEWS_TYPES } from './lib/news/types'
import newsBlogSlugs from './redirects-news-blog.json'

export const redirects: Record<string, string> = {
  '/theliberationist': '/news/blog',
  '/blog': '/news/blog',
  '/news2': '/news',
  '/campaigns/open-rescue': '/open-rescue',
  '/home': '/',
  '/home-new-draft': '/',
  '/petition-test': '/',
  '/checkout': '/donate',
  '/paypal-checkout': '/donate',
  '/order-confirmation': '/donate',
  // 2023 Animal Liberation Conference pages and Webflow site search (not ported).
  '/alc-2023': '/campaigns/animal-liberation-conference',
  '/alc-2023/nominate': '/campaigns/animal-liberation-conference',
  '/alc-2023/shoutouts': '/campaigns/animal-liberation-conference',
  '/alc-2023/shoutouts/activists': '/campaigns/animal-liberation-conference',
  '/alc-2023/shoutouts/chapters': '/campaigns/animal-liberation-conference',
  '/alc-2023/shoutouts/sanctuaries': '/campaigns/animal-liberation-conference',
  '/search': '/news',
  // Old Webflow/Squarespace pages that were never ported.
  '/sign-up': '/take-action',
  '/signup': '/take-action',
  '/get-active-main': '/chapters',
  '/why-direct-action': '/about-us',
  '/materials': '/resources',
  '/press': '/news/press-releases',
  '/press-kit': '/news/press-releases',
  '/press-costcos-bloody-secret': '/news/press-releases',
  '/about-accusations': '/about-us',
  '/about-accusations-against-direct-action-everywhere-dxe': '/about-us',
  '/upcoming-events': '/events',
  // Old campaign and blog pages with a close equivalent among the ported posts.
  '/direct-action-everywhere-evidence':
    '/blog/2016-9-12-dxe-theories-evidence-monitors-activism',
  '/saving-miley': '/blog/2016-12-1-rescued-animal-profile-miley',
  '/egg-farm-360':
    '/blog/2017-1-19-virtual-reality-doc-exposes-the-bloody-horror-of-a-humane-egg-farm',
  '/ucsf': '/blog/2013-9-26-protesting-ucsf',
  '/deadly-feast': '/blog/2015-11-25-qfaeby7ugjraswketwlybegfityt9n',
  '/s/CancelAnimalAg-Policy-Memo.pdf': 'https://dxe.io/caapolicy',
  // Blog posts used to be listed in "DxE in the news" too, at
  // /dxe-in-the-news/<slug>. Those whose slug starts with a date are covered by
  // a pattern below; the rest (src/redirects-news-blog.json, frozen) are listed.
  ...Object.fromEntries(
    newsBlogSlugs.map((slug) => [`/dxe-in-the-news/${slug}`, `/blog/${slug}`]),
  ),
  // Old /theliberationist/YYYY/M/D/<slug> URLs whose posts weren't ported.
  // (Exact matches win over the date-path rule in `patternRedirects`.)
  '/theliberationist/2014/11/3/is-there-a-place-in-animal-rights-for-a-kid-from-china-part-i-performing-whiteness':
    '/news/blog',
  '/theliberationist/2014/11/4/is-there-a-place-in-animal-rights-for-a-kid-from-china-part-ii-orphans-of-the-left':
    '/news/blog',
  '/theliberationist/2015/3/10/what-does-psychology-really-tell-us-about-animal-welfarism':
    '/news/blog',
}

// Pattern rules, tried after the exact matches above. `to` may use $1, $2...
// Blog posts live at /blog/<slug> (they were /theliberationist/<slug>). Ported
// Liberationist posts are named after the old URL with the slashes after the
// year turned into dashes (/theliberationist/2013/11/18/leaving-a-mark ->
// /blog/2013-11-18-leaving-a-mark). Unported posts that aren't listed above
// end up on the 404 page. Press slugs never start with a date (check-content
// enforces it), so the /dxe-in-the-news rule only catches the old blog copies.
export const patternRedirects: { pattern: RegExp; to: string }[] = [
  {
    pattern: /^\/theliberationist\/(\d{4})\/(\d{1,2})\/(\d{1,2})\/([^/]+)$/,
    to: '/blog/$1-$2-$3-$4',
  },
  { pattern: /^\/theliberationist\/([^/]+)$/, to: '/blog/$1' },
  {
    pattern: /^\/dxe-in-the-news\/(\d{4}-\d{1,2}-\d{1,2}-[^/]+)$/,
    to: '/blog/$1',
  },
  { pattern: /^\/upcoming-events\/.+$/, to: '/events' },
]

// Old `/news?type=blog&page=2` URLs: the tab is now in the path
// (`/news/blog?page=2`). `keep` params are carried over when numeric and > 1.
// The old Webflow Spanish whistleblower page was `?language=spanish`.
export const queryRedirects: {
  path: string
  param: string
  to: Record<string, string>
  keep: string[]
}[] = [
  {
    path: '/news',
    param: 'type',
    to: Object.fromEntries(
      NEWS_TYPES.filter((t) => t !== 'all').map((t) => [t, `/news/${t}`]),
    ),
    keep: ['page'],
  },
  {
    path: '/whistleblower-support',
    param: 'language',
    to: { spanish: '/es/whistleblower-support' },
    keep: [],
  },
]

/** Target of a query-string redirect for `pathname`, or undefined. */
export function findQueryRedirect(
  pathname: string,
  get: (param: string) => string | undefined,
) {
  const path = pathname.replace(/(.)\/$/, '$1')
  for (const r of queryRedirects) {
    if (r.path !== path) continue
    // Own keys only: `?type=constructor` must not find Object's.
    const value = get(r.param) ?? ''
    const to = Object.hasOwn(r.to, value) ? r.to[value] : undefined
    if (!to) continue
    const kept = r.keep
      .map((k) => [k, get(k) ?? ''] as const)
      .filter(([, v]) => /^\d+$/.test(v) && Number(v) > 1)
    return kept.length
      ? `${to}?${kept.map(([k, v]) => `${k}=${v}`).join('&')}`
      : to
  }
}

export function findRedirect(pathname: string) {
  const path = pathname.replace(/(.)\/$/, '$1')
  if (redirects[path]) return redirects[path]
  for (const { pattern, to } of patternRedirects)
    if (pattern.test(path)) return path.replace(pattern, to)
}

export type EdgeResult = { redirect: string } | { uri: string }

/**
 * What the CDN does with a request, as the CloudFront function's handler does
 * (see that function's generator): collapse leading slashes (a redirect to
 * `//evil.example` would leave the site), apply redirects, 301 away a trailing
 * slash (keeping the query string), then map the path to the prerendered file.
 * `query` maps each parameter to its raw values, in order, like CloudFront's
 * `multiValue`; the first value is the one redirects look at.
 */
export function resolveRequest(
  rawUri: string,
  query: Record<string, string[]> = {},
): EdgeResult {
  const uri = rawUri.replace(/^[/\\]+/, '/')
  const path = uri.length > 1 && uri.endsWith('/') ? uri.slice(0, -1) : uri

  const to = findRedirect(path) ?? findQueryRedirect(path, (k) => query[k]?.[0])
  if (to) return { redirect: to }
  if (path !== uri) {
    const parts = Object.entries(query).flatMap(([k, values]) =>
      values.map((v) => (v === '' ? k : `${k}=${v}`)),
    )
    return { redirect: path + (parts.length ? `?${parts.join('&')}` : '') }
  }

  if (path === '/') return { uri: '/index.html' }
  return {
    uri: path.split('/').pop()?.includes('.') ? path : `${path}/index.html`,
  }
}
